// Sarge Sprocket (GDD §1): a boxy wind-up robot with a brass moustache and a peaked cap. Minimal frames:
// idle, bob (the whole robot 3% lower) and blink. No gun ever points at him (he holds none).
import { drawParts, fillP, outlineUnits, type Part, part } from './passes.ts';
import { box, circle, line, rrect } from './shapes.ts';
import type { Generator, SargeSpec } from './types.ts';

export const sargeGen: Generator<SargeSpec> = (c, spec, px, env) => {
  const { pal } = env;
  c.save();
  c.scale(px / 100, px / 100);
  if (spec.pose === 'bob') c.translate(0, 3);
  const ow = outlineUnits(env.sprites.outline * 0.8, env.sprites.outlineMinPx, px, env.dpr);
  const brass = pal.sw('ui.brass');
  const ink = pal.hex('ui.ink');
  const parts: Part[] = [];
  const R = (
    tok: string,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number | number[],
    extra: Partial<Part> = {},
  ) => parts.push(part(rrect(x, y, w, h, r), pal.sw(tok), box(x, y, w, h), extra));
  // wind-up key (behind the body)
  R('ui.brass', 80, 58, 12, 6, 2);
  R('ui.brass', 88, 50, 7, 22, 3.5);
  // arms
  R('mach.front', 14, 60, 10, 22, 5);
  R('mach.front', 76, 60, 10, 22, 5);
  // body with sergeant chevrons
  R('mach.front', 22, 56, 56, 38, 8, {
    detail: (cc) => {
      for (let i = 0; i < 2; i++) {
        cc.beginPath();
        line([
          [38, 70 + i * 7],
          [50, 64 + i * 7],
          [62, 70 + i * 7],
        ])(cc);
        cc.strokeStyle = pal.hex('ui.money');
        cc.lineWidth = 3.2;
        cc.lineCap = 'round';
        cc.lineJoin = 'round';
        cc.stroke();
      }
      fillP(cc, circle(50, 86, 3), pal.hex('machine.gallery'));
    },
  });
  // neck
  R('mach.top', 42, 50, 16, 8, 2);
  // head
  R('mach.top', 20, 16, 60, 38, 9, {
    detail: (cc) => {
      const blink = spec.pose === 'blink';
      for (const x of [37, 63]) {
        if (blink) {
          cc.beginPath();
          line([
            [x - 6, 32],
            [x + 6, 32],
          ])(cc);
          cc.strokeStyle = ink;
          cc.lineWidth = 3;
          cc.lineCap = 'round';
          cc.stroke();
        } else {
          fillP(cc, circle(x, 31, 7.5), ink);
          fillP(cc, circle(x, 31, 6), pal.hex('ui.cream'));
          fillP(cc, circle(x + 1, 32, 3), ink);
          fillP(cc, circle(x - 1.5, 29.5, 1.3), pal.hex('ui.cream'));
        }
      }
      // brass moustache
      cc.beginPath();
      cc.moveTo(50, 40);
      cc.bezierCurveTo(44, 36, 34, 37, 30, 44);
      cc.bezierCurveTo(28, 47, 25, 45, 25, 42);
      cc.bezierCurveTo(28, 50, 42, 50, 50, 44);
      cc.bezierCurveTo(58, 50, 72, 50, 75, 42);
      cc.bezierCurveTo(75, 45, 72, 47, 70, 44);
      cc.bezierCurveTo(66, 37, 56, 36, 50, 40);
      cc.closePath();
      cc.fillStyle = brass.outline;
      cc.lineWidth = 2.4;
      cc.strokeStyle = brass.outline;
      cc.stroke();
      cc.fillStyle = brass.base;
      cc.fill();
    },
  });
  // cap with brass badge
  R('ui.buyDark', 18, 6, 64, 14, [9, 9, 2, 2], {
    detail: (cc) => fillP(cc, circle(50, 12, 3.6), brass.base),
  });
  R('ui.buyDark', 14, 17, 40, 5, 2.5);
  drawParts(c, parts, {
    ow,
    rim: false,
    halo: null,
    glow: null,
    shadeBand: pal.rules.shadeBand,
    hiW: 2.6,
  });
  c.restore();
};
